import React, { useState, useEffect, useCallback } from 'react';
import API from '../../utils/api';
import toast from 'react-hot-toast';
import { format, startOfMonth, endOfMonth, subMonths } from 'date-fns';
import { es } from 'date-fns/locale';

const COP = (n) => new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 }).format(Number(n) || 0);

export default function VentasAliaddo() {
  const hoy = new Date();
  const [inicio, setInicio]       = useState(format(startOfMonth(hoy), 'yyyy-MM-dd'));
  const [fin, setFin]             = useState(format(hoy, 'yyyy-MM-dd'));
  const [bodegaId, setBodegaId]   = useState('');
  const [bodegas, setBodegas]     = useState([]);
  const [resumen, setResumen]     = useState([]);
  const [stats, setStats]         = useState(null);
  const [cargando, setCargando]   = useState(false);
  const [importando, setImportando] = useState(false);
  const [resultado, setResultado] = useState(null);
  const [archivo, setArchivo]     = useState(null);
  const [vista, setVista]         = useState('resumen'); // 'resumen' | 'diario' | 'salidas'
  const [fechaSalidas, setFechaSalidas] = useState(format(hoy, 'yyyy-MM-dd'));
  const [salidas, setSalidas]     = useState(null);
  const [cargandoSalidas, setCargandoSalidas] = useState(false);

  useEffect(() => {
    API.get('/bodegas').then(r => setBodegas(r.data)).catch(() => {});
  }, []);

  const cargarSalidas = (fecha, bodega) => {
    if (!bodega) return;
    setCargandoSalidas(true);
    API.get('/ventas/salidas', { params: { bodega_id: bodega, fecha: fecha || fechaSalidas } })
      .then(r => setSalidas(r.data))
      .catch(() => toast.error('Error cargando salidas'))
      .finally(() => setCargandoSalidas(false));
  };

  useEffect(() => {
    if (vista === 'salidas' && bodegaId) cargarSalidas(fechaSalidas, bodegaId);
  }, [vista, fechaSalidas, bodegaId]);

  const cargar = useCallback(() => {
    setCargando(true);
    Promise.all([
      API.get('/ventas/resumen', { params: { inicio, fin, bodega_id: bodegaId || null } }),
      API.get('/ventas/stats',   { params: { inicio, fin } }),
    ]).then(([r, s]) => {
      setResumen(r.data);
      setStats(s.data);
    }).catch(() => toast.error('Error cargando ventas'))
    .finally(() => setCargando(false));
  }, [inicio, fin, bodegaId]);

  useEffect(() => { cargar(); }, [cargar]);

  const handleImportar = async () => {
    if (!archivo) { toast.error('Selecciona un archivo Excel'); return; }
    setImportando(true);
    setResultado(null);
    try {
      const fd = new FormData();
      fd.append('archivo', archivo);
      const r = await API.post('/ventas/importar', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000  // 2 minutos para archivos grandes
      });
      setResultado(r.data);
      toast.success(r.data.mensaje);
      setArchivo(null);
      document.getElementById('input-excel').value = '';
      cargar();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Error al importar');
    } finally { setImportando(false); }
  };

  // Agrupar resumen por día
  const porDia = {};
  resumen.forEach(r => {
    const dia = r.dia?.slice(0, 10);
    if (!porDia[dia]) porDia[dia] = { dia, sucursales: [], total: 0, facturas: 0 };
    porDia[dia].sucursales.push(r);
    porDia[dia].total += parseFloat(r.total_ventas) || 0;
    porDia[dia].facturas += parseInt(r.num_facturas) || 0;
  });
  const diasOrdenados = Object.values(porDia).sort((a, b) => b.dia.localeCompare(a.dia));

  // Resumen por sucursal
  const porSucursal = {};
  resumen.forEach(r => {
    const s = r.sucursal;
    if (!porSucursal[s]) porSucursal[s] = { sucursal: s, total: 0, facturas: 0, efectivo: 0, tarjeta: 0 };
    porSucursal[s].total    += parseFloat(r.total_ventas) || 0;
    porSucursal[s].facturas += parseInt(r.num_facturas) || 0;
    porSucursal[s].efectivo += parseFloat(r.efectivo) || 0;
    porSucursal[s].tarjeta  += parseFloat(r.tarjeta) || 0;
  });

  return (
    <div style={{ fontFamily: "'Segoe UI', sans-serif", maxWidth: 1100, margin: '0 auto' }}>

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 26, fontWeight: 800, color: '#111827' }}>📊 Ventas Aliaddo</h1>
          <p style={{ margin: '4px 0 0', color: '#6B7280', fontSize: 14 }}>Importa y visualiza las ventas de todos los puntos</p>
        </div>
      </div>

      {/* Panel de importación */}
      <div style={{ background: 'white', borderRadius: 14, padding: '18px 22px', boxShadow: '0 1px 4px rgba(0,0,0,0.07)', marginBottom: 22 }}>
        <div style={{ fontWeight: 800, fontSize: 15, marginBottom: 14 }}>📥 Importar reporte de Aliaddo</div>
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 260 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', marginBottom: 5 }}>
              Archivo Excel (.xlsx) exportado desde Aliaddo
            </div>
            <input
              id="input-excel"
              type="file"
              accept=".xlsx,.xls"
              onChange={e => setArchivo(e.target.files[0])}
              style={{ width: '100%', padding: '8px 12px', border: '2px dashed #E5E7EB', borderRadius: 8, fontSize: 13, cursor: 'pointer', boxSizing: 'border-box' }}
            />
          </div>
          <button
            onClick={handleImportar}
            disabled={importando || !archivo}
            style={{ padding: '10px 22px', border: 'none', borderRadius: 8, background: archivo ? 'linear-gradient(135deg,#1a1a2e,#16213e)' : '#E5E7EB', color: archivo ? 'white' : '#9CA3AF', cursor: archivo ? 'pointer' : 'not-allowed', fontSize: 14, fontWeight: 700, whiteSpace: 'nowrap' }}
          >
            {importando ? '⏳ Importando...' : '📤 Importar'}
          </button>
        </div>

        {/* Resultado importación */}
        {resultado && (
          <div style={{ marginTop: 14, background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: 10, padding: '12px 16px', display: 'flex', gap: 20, flexWrap: 'wrap', alignItems: 'center' }}>
            <span style={{ fontSize: 13, color: '#065F46', fontWeight: 700 }}>✅ {resultado.mensaje}</span>
            <div style={{ display: 'flex', gap: 16, fontSize: 12, color: '#374151' }}>
              <span>✅ <strong>{resultado.insertadas}</strong> nuevas</span>
              <span>⏭️ <strong>{resultado.omitidas}</strong> ya existían</span>
              {resultado.errores > 0 && <span style={{ color: '#EF4444' }}>❌ <strong>{resultado.errores}</strong> errores</span>}
            </div>
          </div>
        )}

        <div style={{ marginTop: 10, fontSize: 12, color: '#9CA3AF' }}>
          💡 El archivo puede contener todo el mes — el sistema detecta automáticamente las nuevas facturas y no duplica las que ya existen.
        </div>
      </div>

      {/* Filtros */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 20, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', marginBottom: 4 }}>Desde</div>
          <input type="date" value={inicio} onChange={e => setInicio(e.target.value)}
            style={{ padding: '8px 12px', border: '2px solid #E5E7EB', borderRadius: 8, fontSize: 13, outline: 'none' }} />
        </div>
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', marginBottom: 4 }}>Hasta</div>
          <input type="date" value={fin} onChange={e => setFin(e.target.value)}
            style={{ padding: '8px 12px', border: '2px solid #E5E7EB', borderRadius: 8, fontSize: 13, outline: 'none' }} />
        </div>
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', marginBottom: 4 }}>Punto de venta</div>
          <select value={bodegaId} onChange={e => setBodegaId(e.target.value)}
            style={{ padding: '8px 12px', border: '2px solid #E5E7EB', borderRadius: 8, fontSize: 13, outline: 'none', background: 'white' }}>
            <option value="">Todos</option>
            {bodegas.map(b => <option key={b.id} value={b.id}>{b.nombre}</option>)}
          </select>
        </div>
        {/* Accesos rápidos */}
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {[
            { label: 'Hoy', i: format(hoy,'yyyy-MM-dd'), f: format(hoy,'yyyy-MM-dd') },
            { label: 'Este mes', i: format(startOfMonth(hoy),'yyyy-MM-dd'), f: format(hoy,'yyyy-MM-dd') },
            { label: 'Mes anterior', i: format(startOfMonth(subMonths(hoy,1)),'yyyy-MM-dd'), f: format(endOfMonth(subMonths(hoy,1)),'yyyy-MM-dd') },
          ].map(a => (
            <button key={a.label} onClick={() => { setInicio(a.i); setFin(a.f); }}
              style={{ padding: '8px 12px', border: '2px solid #E5E7EB', borderRadius: 8, background: 'white', cursor: 'pointer', fontSize: 12, fontWeight: 600, color: '#374151' }}>
              {a.label}
            </button>
          ))}
        </div>
      </div>

      {/* Stats globales */}
      {stats && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 12, marginBottom: 22 }}>
          {[
            { label: 'Total ventas', valor: COP(stats.total_ventas), icon: '💰', color: '#10B981' },
            { label: 'Facturas', valor: parseInt(stats.total_facturas).toLocaleString(), icon: '🧾', color: '#3B82F6' },
            { label: 'Ticket promedio', valor: COP(stats.ticket_promedio), icon: '📊', color: '#8B5CF6' },
            { label: 'Efectivo', valor: COP(stats.efectivo), icon: '💵', color: '#F59E0B' },
            { label: 'Tarjeta', valor: COP(stats.tarjeta), icon: '💳', color: '#6366F1' },
          ].map((s, i) => (
            <div key={i} style={{ background: 'white', borderRadius: 12, padding: '14px 16px', boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
              <div style={{ fontSize: 20, marginBottom: 6 }}>{s.icon}</div>
              <div style={{ fontFamily: 'monospace', fontWeight: 800, fontSize: 18, color: s.color }}>{s.valor}</div>
              <div style={{ fontSize: 11, color: '#9CA3AF', fontWeight: 600, marginTop: 2 }}>{s.label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Tabs vista */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 16, background: 'white', borderRadius: 10, padding: 3, boxShadow: '0 1px 4px rgba(0,0,0,0.07)', width: 'fit-content' }}>
        {[{ id: 'resumen', label: '🏪 Por sucursal' }, { id: 'diario', label: '📅 Por día' }, { id: 'salidas', label: '📦 Salidas de inventario' }].map(v => (
          <button key={v.id} onClick={() => setVista(v.id)} style={{ padding: '7px 16px', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: vista === v.id ? 700 : 500, background: vista === v.id ? '#1a1a2e' : 'transparent', color: vista === v.id ? 'white' : '#6B7280' }}>
            {v.label}
          </button>
        ))}
      </div>

      {cargando ? (
        <div style={{ padding: 40, textAlign: 'center', color: '#9CA3AF' }}>Cargando...</div>
      ) : resumen.length === 0 ? (
        <div style={{ background: 'white', borderRadius: 14, padding: 40, textAlign: 'center', color: '#9CA3AF', boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
          <div style={{ fontSize: 36, marginBottom: 12 }}>📭</div>
          <div style={{ fontWeight: 600, fontSize: 15 }}>Sin ventas importadas en este período</div>
          <div style={{ fontSize: 13, marginTop: 6 }}>Importa el reporte Excel de Aliaddo para ver los datos</div>
        </div>
      ) : vista === 'resumen' ? (
        /* Vista por sucursal */
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {Object.values(porSucursal).sort((a,b) => b.total - a.total).map(s => {
            const pctEfectivo = s.total > 0 ? (s.efectivo / s.total * 100) : 0;
            const pctTarjeta  = s.total > 0 ? (s.tarjeta  / s.total * 100) : 0;
            return (
              <div key={s.sucursal} style={{ background: 'white', borderRadius: 14, padding: '18px 22px', boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 10, marginBottom: 14 }}>
                  <div>
                    <div style={{ fontWeight: 800, fontSize: 16 }}>🏪 {s.sucursal}</div>
                    <div style={{ fontSize: 12, color: '#6B7280', marginTop: 2 }}>{s.facturas} facturas</div>
                  </div>
                  <div style={{ fontFamily: 'monospace', fontWeight: 800, fontSize: 22, color: '#10B981' }}>{COP(s.total)}</div>
                </div>
                {/* Barra medios de pago */}
                <div style={{ marginBottom: 8 }}>
                  <div style={{ display: 'flex', height: 8, borderRadius: 4, overflow: 'hidden', background: '#F3F4F6' }}>
                    <div style={{ width: `${pctEfectivo}%`, background: '#10B981' }} />
                    <div style={{ width: `${pctTarjeta}%`,  background: '#3B82F6' }} />
                  </div>
                  <div style={{ display: 'flex', gap: 16, marginTop: 6, fontSize: 12 }}>
                    <span style={{ color: '#10B981', fontWeight: 700 }}>💵 Efectivo: {COP(s.efectivo)} ({pctEfectivo.toFixed(0)}%)</span>
                    <span style={{ color: '#3B82F6', fontWeight: 700 }}>💳 Tarjeta: {COP(s.tarjeta)} ({pctTarjeta.toFixed(0)}%)</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : vista === 'diario' ? (
        /* Vista por día */
        <div style={{ background: 'white', borderRadius: 14, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: '#1a1a2e' }}>
                  {['Día','Sucursal','Facturas','Efectivo','Tarjeta','Total'].map(h => (
                    <th key={h} style={{ padding: '10px 14px', textAlign: h === 'Día' || h === 'Sucursal' ? 'left' : 'right', color: 'white', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {diasOrdenados.map((dia, di) => (
                  dia.sucursales.map((s, si) => (
                    <tr key={`${dia.dia}-${s.sucursal}`} style={{ borderTop: '1px solid #F3F4F6', background: di % 2 === 0 ? 'white' : '#FAFAFA' }}>
                      {si === 0 ? (
                        <td rowSpan={dia.sucursales.length} style={{ padding: '10px 14px', fontWeight: 700, verticalAlign: 'top', borderRight: '1px solid #F3F4F6' }}>
                          <div>{format(new Date(dia.dia + 'T12:00'), "d 'de' MMM", { locale: es })}</div>
                          <div style={{ fontSize: 11, color: '#6B7280', fontWeight: 400 }}>{format(new Date(dia.dia + 'T12:00'), 'EEEE', { locale: es })}</div>
                          <div style={{ fontFamily: 'monospace', fontWeight: 800, color: '#10B981', marginTop: 4 }}>{COP(dia.total)}</div>
                        </td>
                      ) : null}
                      <td style={{ padding: '10px 14px' }}>{s.sucursal}</td>
                      <td style={{ padding: '10px 14px', textAlign: 'right', color: '#6B7280' }}>{s.num_facturas}</td>
                      <td style={{ padding: '10px 14px', textAlign: 'right', fontFamily: 'monospace', color: '#10B981', fontWeight: 600 }}>{COP(s.efectivo)}</td>
                      <td style={{ padding: '10px 14px', textAlign: 'right', fontFamily: 'monospace', color: '#3B82F6', fontWeight: 600 }}>{COP(s.tarjeta)}</td>
                      <td style={{ padding: '10px 14px', textAlign: 'right', fontFamily: 'monospace', fontWeight: 800 }}>{COP(s.total_ventas)}</td>
                    </tr>
                  ))
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}

      {/* Salidas de inventario Aliaddo */}
      {vista === 'salidas' && (
        <>
          <div style={{ display:'flex', gap:12, marginBottom:18, alignItems:'flex-end', flexWrap:'wrap' }}>
            <div>
              <div style={{ fontSize:11, fontWeight:700, color:'#6B7280', textTransform:'uppercase', marginBottom:4 }}>Fecha</div>
              <input type="date" value={fechaSalidas}
                onChange={e => setFechaSalidas(e.target.value)}
                max={format(hoy,'yyyy-MM-dd')}
                style={{ padding:'8px 12px', border:'2px solid #E5E7EB', borderRadius:8, fontSize:13, outline:'none' }} />
            </div>
            <div>
              <div style={{ fontSize:11, fontWeight:700, color:'#6B7280', textTransform:'uppercase', marginBottom:4 }}>Punto de venta</div>
              <select value={bodegaId} onChange={e => setBodegaId(e.target.value)}
                style={{ padding:'8px 12px', border:'2px solid #E5E7EB', borderRadius:8, fontSize:13, outline:'none', background:'white' }}>
                <option value="">Seleccionar PDV...</option>
                {bodegas.filter(b => b.tipo === 'punto_venta').map(b => (
                  <option key={b.id} value={b.id}>{b.nombre}</option>
                ))}
              </select>
            </div>
            {cargandoSalidas && <span style={{ fontSize:13, color:'#6B7280', padding:'8px 0' }}>⏳ Calculando...</span>}
          </div>
          {!bodegaId && (
            <div style={{ background:'white', borderRadius:14, padding:32, textAlign:'center', color:'#9CA3AF', boxShadow:'0 1px 4px rgba(0,0,0,0.07)' }}>
              Selecciona un punto de venta para ver las salidas
            </div>
          )}
          {bodegaId && salidas && salidas.cuadre?.length === 0 && (
            <div style={{ background:'white', borderRadius:14, padding:32, textAlign:'center', color:'#9CA3AF', boxShadow:'0 1px 4px rgba(0,0,0,0.07)' }}>
              Sin ventas de Aliaddo para esta fecha en este PDV.
            </div>
          )}
          {bodegaId && salidas && salidas.cuadre?.length > 0 && (
            <div style={{ background:'white', borderRadius:14, overflow:'hidden', boxShadow:'0 1px 4px rgba(0,0,0,0.07)' }}>
              <div style={{ background:'#1a1a2e', padding:'12px 20px', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                <div style={{ color:'white', fontWeight:800, fontSize:14 }}>
                  📦 Salidas Aliaddo — {format(new Date(fechaSalidas+'T12:00'),"d 'de' MMMM yyyy",{locale:es})}
                </div>
                <div style={{ color:'rgba(255,255,255,0.6)', fontSize:12 }}>
                  {salidas.cuadre.filter(c => c.consumo_teorico > 0).length} materias primas · {salidas.total_ventas || 0} unds vendidas
                </div>
              </div>
              <div style={{ overflowX:'auto' }}>
                <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
                  <thead>
                    <tr style={{ background:'#F9FAFB' }}>
                      <th style={{ padding:'10px 14px', textAlign:'left', fontSize:11, fontWeight:700, color:'#6B7280', textTransform:'uppercase' }}>Materia prima</th>
                      <th style={{ padding:'10px 14px', textAlign:'right', fontSize:11, fontWeight:700, color:'#6B7280', textTransform:'uppercase' }}>Unidad</th>
                      <th style={{ padding:'10px 14px', textAlign:'right', fontSize:11, fontWeight:700, color:'#6B7280', textTransform:'uppercase' }}>Consumo teórico</th>
                    </tr>
                  </thead>
                  <tbody>
                    {salidas.cuadre
                      .filter(c => c.consumo_teorico > 0)
                      .sort((a,b) => b.consumo_teorico - a.consumo_teorico)
                      .map((c,i) => (
                      <tr key={c.item_id} style={{ borderTop:'1px solid #F3F4F6', background:i%2===0?'white':'#FAFAFA' }}>
                        <td style={{ padding:'10px 14px', fontWeight:600, color:'#111827' }}>{c.nombre}</td>
                        <td style={{ padding:'10px 14px', textAlign:'right', color:'#6B7280', fontSize:12 }}>{c.unidad || '—'}</td>
                        <td style={{ padding:'10px 14px', textAlign:'right', fontFamily:'monospace', fontWeight:800, color:'#111827', fontSize:14 }}>
                          {Math.round(c.consumo_teorico)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
